// Chemin relatif : ce fichier est aussi lu par les tests de bout en bout, qui ne connaissent pas l'alias « @ ».
import { PERSONAS, type PersonaId } from '../../demo/monde';
import {
  canAdminister,
  canBill,
  canHandleRequests,
  canRequest,
  peutSaisirTemps,
  type AccountKind,
  type Role,
} from '@its/shared';

/**
 * LA VISITE GUIDÉE, déclarée ici : chapitres → étapes. Même grammaire que
 * Plans d'actions (`tourSteps.ts`) et l'outil IGD (`chapitres.ts`), pour
 * qu'un chapitre se relise de la même façon d'un outil à l'autre.
 *
 * Une étape vise un élément par sélecteur (convention `data-tour="clé"`), peut
 * imposer une route (le moteur y navigue avant d'afficher la bulle) et un RÔLE
 * (le moteur fait endosser ce rôle dans la démo). Sans cible, la bulle
 * s'affiche au centre.
 *
 * Les chapitres suivent le chemin d'une demande : la cliente la dépose,
 * l'intervenant la traite et compte son temps, la facturation clôt le mois,
 * l'administrateur tient les comptes. Chaque texte dit ce que fait le geste
 * ET ce qu'il entraîne : c'est ce qu'on ne devine pas en cliquant.
 */

export type Placement = 'haut' | 'bas' | 'auto';

/**
 * Avance automatique d'une étape « à vous de jouer » : dès que le geste
 * attendu est fait, la visite passe seule à la suite.
 *  - `route`        : l'utilisateur a changé d'écran ;
 *  - `apparait`     : un élément est apparu ;
 *  - `modaleFermee` : une fenêtre ouverte pendant l'étape s'est refermée.
 */
export type Avance = { type: 'route' } | { type: 'apparait'; selecteur: string } | { type: 'modaleFermee' };

export interface Etape {
  cible?: string;
  route?: string;
  /** Rôle à endosser AVANT d'afficher l'étape. */
  persona?: PersonaId;
  titre: string;
  texte: string;
  placement?: Placement;
  /** Consigne « à vous de jouer », en encart. */
  consigne?: string;
  avance?: Avance;
}

export interface Chapitre {
  id: string;
  titre: string;
  description: string;
  icone: string;
  /** Le rôle dans lequel le chapitre se joue. */
  persona: PersonaId;
  /**
   * À qui le chapitre s'adresse : la règle de `@its/shared` qui ouvre l'écran
   * qu'il enseigne, jamais une liste de rôles recopiée ici (règle 2). Absente :
   * tout le monde.
   */
  peut?: (roles: readonly Role[]) => boolean;
  /**
   * Le PUBLIC du chapitre. `equipe` : réservé au staff IT Solutions — un client
   * n'y voit jamais la file, les clients, le temps ni la facturation, même dans
   * le monde fictif. `client` : la version client d'un chapitre de l'équipe.
   * Absent : tout le monde (sous réserve de `peut`).
   */
  audience?: 'client' | 'equipe';
  etapes: Etape[];
}

// Les demandes du monde fictif qu'on ouvre directement (demo/monde.ts).
const IMPRIMANTE = '/demandes/d0000000-0000-4000-8000-000000000301';
const VPN = '/demandes/d0000000-0000-4000-8000-000000000302';
const CABINET = '/clients/d0000000-0000-4000-8000-000000000101';

export const CHAPITRES: Chapitre[] = [
  {
    id: 'prise-en-main',
    titre: 'Prise en main',
    description: 'Se repérer : le menu selon les rôles, votre profil et vos préférences, le bandeau de démo.',
    icone: '🧭',
    persona: 'intervenant',
    audience: 'equipe',
    etapes: [
      {
        titre: 'Bienvenue dans la borne technique',
        texte:
          'Vous êtes dans un service d’assistance fictif : trois cabinets clients, une équipe d’intervenants, un mois à facturer. ' +
          'Tout ce que vous faites ici reste dans votre navigateur : rien n’atteint les vraies données, aucun courriel ne part. ' +
          'Vous commencez dans la peau de Thomas LEROUX, intervenant.',
      },
      {
        cible: 'aside',
        route: '/mes-demandes',
        titre: 'Le menu suit vos rôles',
        texte:
          'Un client voit « Mes demandes », d’où il dépose une nouvelle demande. Un intervenant ajoute la file, les demandes périodiques, les clients et son temps. ' +
          'La facturation voit la pré-facturation ; l’administrateur, les comptes. Masquer une entrée n’est qu’un confort : c’est le serveur qui refuse.',
      },
      {
        cible: '[data-tour="profil"]',
        placement: 'bas',
        titre: 'Votre profil et vos préférences',
        texte:
          'Le menu du profil dit qui vous êtes pour la borne. La NATURE du compte se constate — staff IT Solutions, collaborateur du groupe ou client ; ' +
          'les RÔLES se donnent dans l’administration, et la nature les borne. On y règle aussi le thème, la taille du texte et le contraste. ' +
          'Les autres outils du groupe (borne RH, gestion des actifs, borne commerciale) sont dans le menu, sous « Mes autres outils ».',
      },
      {
        cible: '[data-tour="bandeau-demo"]',
        placement: 'haut',
        titre: 'Le bandeau de la démo',
        texte:
          'Il rappelle en permanence que les données sont fictives. Il fait changer de rôle (cliente, intervenant, facturation, administrateur), ' +
          'remet la démo à zéro, rouvre cette visite, ou vous fait sortir.',
      },
      {
        titre: 'Le chemin d’une demande',
        texte:
          'Les chapitres suivent une demande de bout en bout : la cliente la dépose, l’intervenant la traite et compte son temps, ' +
          'la facturation clôt le mois et alimente Ekivox, l’administrateur tient les comptes. Commencez par « Déposer et suivre une demande ».',
      },
    ],
  },
  {
    id: 'prise-en-main-client',
    titre: 'Prise en main',
    description: 'Se repérer : votre menu, votre profil et vos préférences, le bandeau de démo.',
    icone: '🧭',
    persona: 'demandeur',
    audience: 'client',
    etapes: [
      {
        titre: 'Bienvenue dans la borne d’assistance',
        texte:
          'Vous êtes dans une démonstration : un cabinet fictif et ses demandes d’assistance. ' +
          'Tout ce que vous faites ici reste dans votre navigateur : rien n’atteint les vraies données, aucun courriel ne part. ' +
          'Vous êtes Sophie MARCHAND, cliente d’IT Solutions.',
      },
      {
        cible: 'aside',
        route: '/mes-demandes',
        titre: 'Votre menu',
        texte:
          '« Mes demandes » rassemble les demandes de votre cabinet : celles que vous avez déposées et celles de vos collègues. ' +
          'C’est de là que vous en déposez une nouvelle. Le menu « Aide » donne accès aux documents d’IT Solutions.',
      },
      {
        cible: '[data-tour="profil"]',
        placement: 'bas',
        titre: 'Votre profil et vos préférences',
        texte:
          'Le menu du profil rappelle votre compte. On y règle aussi le thème, la taille du texte et le contraste, ' +
          'et on s’y déconnecte.',
      },
      {
        cible: '[data-tour="bandeau-demo"]',
        placement: 'haut',
        titre: 'Le bandeau de la démo',
        texte:
          'Il rappelle en permanence que les données sont fictives. Il remet la démo à zéro, rouvre cette visite, ou vous fait sortir.',
      },
      {
        titre: 'Et maintenant',
        texte: 'Passez au chapitre « Déposer et suivre une demande » : vous y déposez une demande, suivez son avancement et répondez à l’équipe.',
      },
    ],
  },
  {
    id: 'deposer',
    titre: 'Déposer et suivre une demande',
    description: 'Côté client : déposer, suivre l’avancement, répondre à une question, clôturer ou rouvrir.',
    icone: '📨',
    persona: 'demandeur',
    peut: canRequest,
    etapes: [
      {
        cible: '[data-tour="liste-demandes"]',
        route: '/mes-demandes',
        titre: 'Les demandes de votre cabinet',
        texte:
          'Sophie MARCHAND, cliente, voit les demandes de son cabinet et leur état. Une demande « En attente du demandeur » attend une réponse de sa part.',
      },
      {
        cible: '[data-tour="mes-entete"]',
        titre: 'Filtrer, ou déposer',
        texte: 'Le filtre d’état est retenu d’une visite à l’autre. « Nouvelle demande » est toujours à portée de main.',
        consigne: 'Cliquez sur « Nouvelle demande ».',
        // Le formulaire s'ouvre en modale, sans changer d'écran : c'est son apparition qui fait avancer.
        avance: { type: 'apparait', selecteur: '[data-tour="nd-type-urgence"]' },
      },
      {
        cible: '[data-tour="nd-type-urgence"]',
        route: '/nouvelle-demande',
        titre: 'Type et urgence',
        texte:
          'L’urgence fixe le délai de prise en charge que l’équipe s’engage à tenir, en heures ouvrées : 4 h pour une demande bloquante, ' +
          '3 jours pour une modérée, 5 jours pour une faible. « Bloquante » veut dire : vous ne pouvez plus travailler.',
      },
      {
        cible: '[data-tour="nd-texte"]',
        titre: 'Ce qui ne marche pas',
        texte:
          'L’objet tient en une phrase ; la description dit ce que vous voyez, depuis quand, et ce que vous avez essayé. ' +
          'Un refus dit toujours ce qui manque : un objet trop court est refusé avec la longueur attendue.',
      },
      {
        cible: '[data-tour="nd-pieces"]',
        titre: 'Une capture vaut mieux qu’un long texte',
        texte:
          'Jusqu’à 10 fichiers de 20 Mo : images, PDF, bureautique, archives, courriels. Les exécutables sont refusés — le fichier sera ouvert par quelqu’un d’autre.',
      },
      {
        cible: '[data-tour="nd-envoyer"]',
        titre: 'Envoyer',
        texte:
          'La demande naît « Émise », avec un numéro (BT-année-numéro). Elle apparaît aussitôt dans la file de l’équipe, dans la colonne de votre société.',
        consigne: 'Remplissez type, urgence, objet et description, puis « Envoyer la demande ».',
        avance: { type: 'route' },
      },
      {
        cible: '[data-tour="fiche-frise"]',
        titre: 'Où en est votre demande',
        texte:
          'Cinq états : Émise → Prise en charge ⇄ En attente du demandeur, Gelée, Clôturée. Un intervenant la fait avancer ; vous, vous pouvez la clôturer, ' +
          'et la rouvrir pendant 5 jours ouvrés si le problème revient.',
      },
      {
        cible: '[data-tour="fiche-repondre"]',
        route: VPN,
        titre: 'Une question vous attend',
        texte:
          'Sur cette autre demande, l’intervenant vous a posé une question : elle est « En attente du demandeur ». Répondre la renvoie automatiquement chez lui, en « Prise en charge ».',
        consigne: 'Écrivez une réponse (par exemple « Le poste est allumé, vous pouvez intervenir. ») et envoyez-la.',
        // La frise porte l'identifiant ET l'état de la demande affichée : l'écran montre un instant la
        // demande d'avant en arrivant, un critère moins précis ferait sauter l'étape.
        avance: { type: 'apparait', selecteur: `[data-tour="fiche-frise"][data-demande="${VPN.split('/').pop()}"][data-etat="prise_en_charge"]` },
      },
      {
        cible: '[data-tour="fiche-fil"]',
        titre: 'Le fil des échanges',
        texte:
          'Messages, interventions et changements d’état, dans l’ordre. Vous ne voyez ni le temps passé ni les notes internes de l’équipe : ' +
          'ce n’est pas masqué à l’écran, c’est absent de ce que le serveur vous envoie.',
      },
      {
        cible: '[data-tour="fiche-actions"]',
        titre: 'Clôturer vous-même',
        texte:
          'Quand c’est réglé, clôturez — même si personne ne l’a encore prise en charge : l’équipe le voit. ' +
          'Une demande clôturée se rouvre pendant 5 jours ouvrés ; au-delà, déposez-en une nouvelle en citant celle-ci.',
      },
    ],
  },
  {
    id: 'traiter',
    titre: 'Traiter la file',
    description: 'Côté intervenant : la file, prendre en charge, répondre, saisir une intervention, faire avancer l’état.',
    icone: '🛠️',
    persona: 'intervenant',
    peut: canHandleRequests,
    audience: 'equipe',
    etapes: [
      {
        cible: '[data-tour="file-colonnes"]',
        route: '/file',
        titre: 'Deux files côte à côte : clients et internes',
        texte:
          'Les demandes des clients à gauche, celles des entités du groupe à droite, visibles d’un coup. « Nouvelle demande » permet aussi d’en ouvrir une pour le compte d’un client qui appelle.',
      },
      {
        cible: '[data-tour="file-filtres"]',
        titre: 'Filtrer sans perdre ses réglages',
        texte:
          'La file s’ouvre toujours sur les demandes émises, celles qui attendent d’être prises. Période, intervenant (« moi » pour ses propres demandes) et type sont retenus d’une visite à l’autre. La file se met à jour en direct quand un collègue agit.',
      },
      {
        cible: '[data-tour="liste-demandes"]',
        titre: 'Décider quoi traiter',
        texte: 'L’urgence, la société, qui l’a prise et depuis quand : de quoi choisir sans ouvrir chaque fiche.',
      },
      {
        cible: '[data-tour="fiche-actions"]',
        route: IMPRIMANTE,
        titre: 'Prendre en charge',
        texte:
          'Cette demande est « Émise » : personne ne l’a prise. La prendre en charge vous l’assigne et la passe « Prise en charge ». Dans l’outil, la cliente en est prévenue par courriel (un lien seul, jamais le contenu) ; en démo, rien ne part.',
        consigne: 'Cliquez sur « Prendre en charge ».',
      },
      {
        cible: '[data-tour="fiche-repondre"]',
        titre: 'Répondre, ou noter pour l’équipe',
        texte:
          'Un message public part vers la cliente. Basculé en « Interne à l’équipe », il reste entre intervenants : il n’est jamais envoyé au demandeur.',
      },
      {
        cible: '[data-tour="fiche-actions"]',
        titre: 'Saisir une intervention',
        texte:
          'Une intervention compte le temps passé (jusqu’à 12 h, par quarts d’heure — zéro si rien à déclarer), le temps non facturable en plus, la prestation Ekivox — choisie parmi les prestations en cours du client, lues chez Ekivox ; sans elle, l’intervention attend son affectation —, ' +
          'et les autres intervenants présents : facturée une fois au client, comptée pour chacun. Elle peut faire avancer l’état dans le même geste.',
        consigne: 'Cliquez sur « Saisir une intervention », remplissez-la, enregistrez.',
        avance: { type: 'modaleFermee' },
      },
      {
        cible: '[data-tour="fiche-suivi"]',
        titre: 'Le temps s’additionne',
        texte: 'Le temps passé de la demande est la somme de ses interventions : il se calcule, il ne se saisit jamais.',
      },
      {
        cible: '[data-tour="fiche-actions"]',
        titre: 'Faire avancer l’état',
        texte:
          '« En attente du demandeur » quand vous attendez une réponse ; « Gelée » quand on attend autre chose (une livraison…) — le motif est obligatoire ; ' +
          '« Clôturée » quand c’est réglé. Une transition impossible est refusée avec les choix possibles.',
      },
      {
        cible: '[data-tour="fiche-pj"]',
        titre: 'Les pièces jointes',
        texte: 'La capture de la cliente est là. Les vôtres peuvent être « Réservées à l’équipe » : la cliente ne les verra pas.',
      },
      {
        cible: '[data-tour="periodiques-tableau"]',
        route: '/periodiques',
        titre: 'Les demandes périodiques',
        texte:
          'Un contrôle mensuel, un point hebdomadaire : la règle recrée la demande à chaque échéance. Suspendue, elle ne crée plus rien ; réactivée, elle repart de la prochaine échéance, sans rattrapage.',
      },
    ],
  },
  {
    id: 'temps',
    titre: 'Compter son temps',
    description: 'La feuille de la semaine, le temps interne, les interventions à plusieurs, la recherche et les exports.',
    icone: '⏱️',
    persona: 'intervenant',
    peut: peutSaisirTemps,
    audience: 'equipe',
    etapes: [
      {
        cible: '[data-tour="temps-semaine"]',
        route: '/temps',
        titre: 'Votre semaine',
        texte:
          'Du lundi au vendredi, le cumul de chaque jour (temps passé + non facturable). Une intervention où vous étiez PRÉSENT, saisie par un collègue, ' +
          'compte aussi ici : « Présent · saisie par… ». Elle se corrige chez son auteur.',
      },
      {
        cible: '[data-tour="temps-total"]',
        titre: 'Le total de la semaine',
        texte: 'Calculé, jamais saisi : il suit chaque ajout, chaque correction.',
      },
      {
        cible: '[data-tour="temps-ajouter"]',
        titre: 'Du temps sans demande',
        texte:
          'Une réunion, de la veille, de la gestion interne : du temps INTERNE, sans client. Ou du temps pour un client hors demande. Une date future est refusée. Un client sans mission Ekivox (Boulangerie Lemoine) ? Le temps est mis d’office en attente d’affectation : demandez sa mission AIDE0305 depuis le champ prestation, et la feuille vous rappelle de le rattacher.',
        consigne: 'Cliquez sur « Ajouter du temps », saisissez une réunion en temps interne, enregistrez.',
        avance: { type: 'modaleFermee' },
      },
      {
        cible: '[data-tour="interventions-filtres"]',
        route: '/interventions',
        titre: 'Retrouver des interventions',
        texte:
          'Une recherche libre (client, description, numéro de demande, intervenant), puis la période, le type, le client (ou le temps interne) et l’intervenant. Chercher les interventions de quelqu’un inclut celles où il était présent.',
      },
      {
        cible: '[data-tour="interventions-indicateurs"]',
        titre: 'Des indicateurs sur tout le filtre',
        texte: 'Nombre, temps passé, temps non facturable : calculés sur tout ce que le filtre retient, pas seulement sur la page affichée.',
      },
      {
        cible: '[data-tour="interventions-par-client"]',
        titre: 'Le temps de chaque client',
        texte:
          'Chaque client qui a des interventions sur le filtre, le plus chargé d’abord, avec son temps passé et sa part du total. Cliquer un client n’affiche que ses interventions ; le recliquer revient à tous.',
      },
      {
        cible: '[data-tour="interventions-exports"]',
        titre: 'Exporter',
        texte:
          'CSV ou XLSX, mêmes colonnes. Les durées sortent en heures décimales ET en h:mm, en nombres qu’Excel additionne. En démo, le fichier porte « DEMO » dans son nom.',
      },
    ],
  },
  {
    id: 'clients',
    titre: 'Clients et accès',
    description: 'Retrouver un client, lire ses contacts, ouvrir ou retirer l’accès d’un contact à la borne.',
    icone: '🏢',
    persona: 'intervenant',
    // La facturation consulte les clients, mais ce chapitre enseigne surtout l'ouverture
    // d'accès, réservée à l'équipe d'assistance : c'est elle qui le reçoit.
    peut: canHandleRequests,
    audience: 'equipe',
    etapes: [
      {
        cible: '[data-tour="clients-recherche"]',
        route: '/clients',
        titre: 'Les clients viennent d’Ekivox',
        texte:
          'La borne les LIT : raison sociale, code dossier, contacts. Une correction se fait dans Ekivox, jamais ici — une copie divergerait au premier changement.',
        consigne: 'Tapez « march » pour retrouver le Cabinet Marchand.',
        avance: { type: 'apparait', selecteur: '[data-tour="clients-resultats"] a' },
      },
      {
        cible: '[data-tour="client-contacts"]',
        route: CABINET,
        titre: 'Ouvrir un accès à un contact',
        texte:
          'Un contact client reçoit un lien pour choisir son mot de passe ; un salarié du groupe entre par Microsoft, il n’y a rien à lui ouvrir. ' +
          'Un contact qui manque se crée avec « Nouveau contact » : nom, prénom et adresse e-mail suffisent, le téléphone est facultatif.',
        consigne: 'Ouvrez un accès à Paul VIDAL. En démo, le courriel ne part pas : la borne le dit.',
      },
      {
        titre: 'Retirer un accès',
        texte:
          'Un accès retiré ferme la porte sans rien effacer : les demandes du contact restent, et l’accès se rouvre d’un clic. Seule l’équipe d’assistance ouvre et retire les accès.',
      },
    ],
  },
  {
    id: 'facturer',
    titre: 'Facturer le mois',
    description: 'Côté facturation : l’avancement du mois, l’aperçu Ekivox, le fichier d’import, le relevé et le gel.',
    icone: '🧾',
    persona: 'facturation',
    peut: canBill,
    audience: 'equipe',
    etapes: [
      {
        cible: '[data-tour="pf-avancement"]',
        route: '/prefacturation',
        titre: 'Où en est le mois',
        texte:
          'Nadia BERGER, facturation : combien de clients ont du temps facturable le mois dernier, combien ont reçu leur relevé, combien de lignes attendent le fichier Ekivox.',
      },
      {
        cible: '[data-tour="pf-filtres"]',
        route: '/prefacturation',
        titre: 'Le mois à préparer',
        texte: 'La pré-facturation s’ouvre sur le mois précédent. Une période, c’est un mois calendaire.',
      },
      {
        cible: '[data-tour="pf-tuiles"]',
        titre: 'Ce qui part, et ce qui reste',
        texte:
          'Seules les interventions rattachées à une prestation Ekivox partent dans le fichier d’import. Le temps client sans prestation reste dans la feuille de temps : il est compté ici pour ne pas être perdu de vue.',
      },
      {
        cible: '[data-tour="pf-tableau"]',
        route: '/prefacturation',
        titre: 'Corriger depuis l’aperçu',
        texte:
          'Chaque ligne montre sa prestation Ekivox et la mission qui la porte, relues chez Ekivox. Chaque ligne se corrige sans quitter l’écran : « Corriger » rouvre l’intervention, et l’aperçu se recalcule. La facturation peut corriger une ligne qui n’est pas encore partie.',
      },
      {
        cible: '[data-tour="pf-exporter"]',
        placement: 'bas',
        titre: 'Le fichier « Vers Ekivox »',
        texte:
          'Il produit le fichier à importer dans Ekivox : dossier, mission, code prestation et temps — mission et code viennent de la prestation choisie. Une ligne exportée ne repart jamais, et elle est FIGÉE : la corriger devient une régularisation, ' +
          'qui partira en contre-passation dans le prochain fichier.',
        consigne: 'Cliquez sur « Vers Ekivox ».',
      },
      {
        cible: '[data-tour="pf-exporter-lignes"]',
        placement: 'bas',
        titre: 'Le bilan du mois, dans un tableur',
        texte:
          'Le mois et le client choisis partent dans un fichier XLSX : les lignes affichées, régularisations comprises, et dans le même onglet les interventions qui ne sont pas facturées, avec leur motif. Le total facturé et celui du mois sont en pied, séparés. Rien n’est marqué exporté.',
      },
      {
        cible: '[data-tour="pf-client"]',
        titre: 'Le relevé d’un client',
        texte: 'Le relevé liste les interventions du mois pour un client. Il part depuis la boîte du support, pour que le client réponde à l’équipe.',
        consigne: 'Choisissez « Cabinet Marchand & Associés », puis « Afficher ».',
        avance: { type: 'apparait', selecteur: '[data-tour="pf-releve"]' },
      },
      {
        cible: '[data-tour="pf-releve"]',
        titre: 'Envoyer, et figer le mois',
        texte:
          'L’aperçu PDF ne fige rien. L’envoi, lui, FIGE le mois pour ce client : plus personne ne modifie ses interventions, sauf la facturation, avec un motif — ' +
          'et la correction devient une régularisation, portée au relevé suivant. Renvoyer crée une version 2 ; la 1 reste consultable.',
        consigne: 'Faites un aperçu PDF, puis envoyez le relevé. En démo, aucun courriel ne part.',
      },
      {
        titre: 'Ce que le gel protège',
        texte:
          'Ce qui a quitté la borne (un relevé envoyé, une ligne partie vers Ekivox) ne se réécrit pas en silence : sinon le client, Ekivox et la borne diraient trois choses différentes. ' +
          'Dans l’outil, cette règle est gardée par la base elle-même, quel que soit le chemin d’écriture.',
      },
    ],
  },
  {
    id: 'administrer',
    titre: 'Administrer les comptes',
    description: 'La nature d’un compte, les rôles qu’on donne, ceux qui sont ignorés, les garde-fous.',
    icone: '🔐',
    persona: 'administrateur',
    peut: canAdminister,
    audience: 'equipe',
    etapes: [
      {
        cible: '[data-tour="comptes-tableau"]',
        route: '/administration/comptes',
        titre: 'Les comptes et leur nature',
        texte:
          'Julien FAURE, administrateur. Chaque compte a une NATURE constatée (staff, collaborateur, client) et des RÔLES donnés. ' +
          'Un rôle donné que la nature ne peut pas tenir est signalé « ignoré » — jamais appliqué.',
      },
      {
        cible: '[data-tour="comptes-filtres"]',
        titre: 'Retrouver un compte',
        texte: 'Par nom, adresse ou nature.',
      },
      {
        cible: '[data-tour="comptes-tableau"]',
        titre: 'Donner un rôle',
        texte:
          'La fenêtre ne propose que les rôles que la nature du compte peut tenir : une cliente n’a aucun autre rôle que demandeur. ' +
          'Deux garde-fous : on ne désactive pas son propre compte, et le dernier administrateur actif ne perd pas son rôle.',
        consigne: 'Cliquez sur « Modifier » sur le compte de Léa ROUSSEL, donnez-lui « Facturation », enregistrez.',
        avance: { type: 'modaleFermee' },
      },
      {
        titre: 'Vous avez fait le tour',
        texte:
          'Vous pouvez rejouer n’importe quel chapitre, changer de rôle depuis le bandeau, ou remettre la démo à zéro. « Quitter la démo » vous ramène à vos vraies données.',
      },
    ],
  },
];

/**
 * QUI VOIT LA VISITE DE L'ÉQUIPE : le staff IT Solutions, et lui seul. Un
 * client — et quiconque découvre la borne SANS compte, depuis l'écran de
 * connexion, où l'on ne sait pas qui regarde — ne voit que ce qu'un client
 * voit : ni la file, ni les autres clients, ni le temps, ni la facturation
 * (C. Hayer, 20/10/2026). Un collaborateur du groupe dépose des demandes comme
 * un client.
 */
export const voitLaVisiteDeLEquipe = (nature: AccountKind | null): boolean => nature === 'staff';

/**
 * Les chapitres qui concernent quelqu'un, d'après sa NATURE et ses rôles
 * RÉELS — pas ceux du personnage qu'il endosse dans la démo.
 */
export function chapitresPour(roles: readonly Role[] | null, nature: AccountKind | null): Chapitre[] {
  const equipe = voitLaVisiteDeLEquipe(nature);
  const r: readonly Role[] = equipe ? (roles ?? []) : ['demandeur'];
  return CHAPITRES.filter((c) => (equipe ? c.audience !== 'client' : c.audience !== 'equipe') && (!c.peut || c.peut(r)));
}

/** Les personnages qu'on peut endosser dans la démo : la cliente seule, hors staff. */
export function personasPour(nature: AccountKind | null): readonly PersonaId[] {
  return voitLaVisiteDeLEquipe(nature) ? PERSONAS : ['demandeur'];
}

export function chapitreParId(id: string): Chapitre | null {
  return CHAPITRES.find((c) => c.id === id) ?? null;
}
